/**
 * useInPageScroll — single source of truth for smooth-scrolling to an
 * in-page section on campaign pages (v1 + v2).
 *
 * The offset math + magic numbers below used to be copy-pasted across four
 * components (V2Hero.scrollToSupport, DigPinkCampaignPageV2.openSupportModalFromSticky,
 * TopHeaderV2.scrollToSection, and the v1 TopHeader.scrollToSection). If the
 * sticky-header height ever changed, every copy had to be edited in lockstep
 * or in-page anchors would silently land at the wrong y-position. Centralizing
 * here means a header-height change is a one-line edit and every caller stays
 * aligned, so the "every in-page anchor lands at the same y-position" promise
 * actually holds.
 */

// Below this viewport width we use the mobile offsets. Matches the
// campaign-page mobile breakpoint used elsewhere in the v2 design system.
const MOBILE_BREAKPOINT = 1024

// Sticky-header height (px) at each breakpoint — how far the fixed header
// overlaps the top of the viewport and therefore must be subtracted.
const HEADER_HEIGHT = { mobile: 80, desktop: 97 }

// Extra "breathing room" gap (px) below the header so a section's eyebrow /
// heading lands cleanly under the bar instead of butting right up against it.
const BREATHING_ROOM = { mobile: 80, desktop: 20 }

/**
 * Smooth-scroll the window so an in-page section lands just below the sticky
 * header (header height + breathing-room gap).
 *
 * @param {string|Element} idOrEl - the target element, or its DOM id (no
 *   leading '#').
 * @param {Event} [event] - optional click event. preventDefault() is called
 *   ONLY when the target is found, so a missing target falls through to the
 *   browser's native href fragment-jump — preserving the no-JS fallback for
 *   anchor (<a>) callers.
 * @returns {boolean} true if a target was found and scrolled to; false
 *   otherwise, letting callers run their own fallback (native jump, modal
 *   emit, error log, …).
 */
export function scrollToInPageAnchor(idOrEl, event) {
  const target = typeof idOrEl === 'string'
    ? document.getElementById(idOrEl)
    : idOrEl
  if (!target) return false
  if (event) event.preventDefault()

  const isMobile = window.innerWidth < MOBILE_BREAKPOINT
  const headerHeight = isMobile ? HEADER_HEIGHT.mobile : HEADER_HEIGHT.desktop
  const breathingRoom = isMobile ? BREATHING_ROOM.mobile : BREATHING_ROOM.desktop
  const rect = target.getBoundingClientRect()
  const top = window.pageYOffset + rect.top - headerHeight - breathingRoom
  window.scrollTo({ top, behavior: 'smooth' })
  return true
}
